import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../services/api";
import type { WalletEntry } from "../types/wallet";
import { PAYMENT_BASE_URL, walletKeys } from "./queryKeys";

export interface RecordRefundUtrInput {
  entryId: number;
  /** The entry's facility — its wallet balance moves when the refund posts. */
  facilityId: number;
  utrNumber: string;
}

/**
 * Records the bank UTR for an expired open-advance entry (ENG-27799). No
 * `amount`: VIP then refunds the whole residual, the only shape it completes.
 * Terminal — there is no un-refund. Sending the same UTR again is a no-op 200.
 */
export function recordRefundUtr({ entryId, utrNumber }: RecordRefundUtrInput): Promise<WalletEntry> {
  return api.post(`${PAYMENT_BASE_URL}/wallet/entries/${entryId}/refund-utr`, {
    utr_number: utrNumber,
  });
}

/**
 * Refetches on settle, not just on success: the 200 carries the bare entry
 * (no adjustments or names), and a 404 / 409 means the entry changed under the
 * user, so the list is re-read either way. The facility's wallet balance moves
 * too — the refund posts a Gringotts credit (ENG-27743).
 */
export function useRecordRefundUtr() {
  const queryClient = useQueryClient();
  return useMutation((input: RecordRefundUtrInput) => recordRefundUtr(input), {
    onSettled: (_data, _error, input) => {
      queryClient.invalidateQueries(walletKeys.entriesAll());
      queryClient.invalidateQueries(walletKeys.balance(input.facilityId));
    },
  });
}
